import { useDataSourceStore } from '@/stores/dataSource';
import { usePlayerProfileRequestsStore } from '@/stores/playerProfileRequests';
import { dataSourceMixin } from '@/services/dataSourceMixin';
import baseNavbar from './NavbarV2.base';
import PublicSidePanel from '@/components/public/PublicSidePanel/PublicSidePanel.vue';
import FrontendService from '@/services/FrontendService';
import LanguageSwitcher from '@/components/layout/LanguageSwitcher/LanguageSwitcher.vue';
import Offcanvas from 'bootstrap/js/src/offcanvas.js';
import TabToggle from '@/components/ui/TabToggle/TabToggle.vue';
import { SERVICE_BRAND } from '@/config';

export default {
  mixins: [dataSourceMixin],
  ...baseNavbar,
  name: 'NavbarV2',
  watch: {
    ...(baseNavbar.watch || {}),
    profileRequestIdentity: { immediate: true, handler: 'refreshProfileRequests' },
  },
  components: {
    ...(baseNavbar.components || {}),
    PublicSidePanel,
    LanguageSwitcher,
    TabToggle,
  },

  data() {
    return {
      ...(baseNavbar.data ? baseNavbar.data.call(this) : {}),
      themeMode: FrontendService.getThemeMode(),

      mobileMenuOpen: false,
      mobileSection: '',
      mobileProfileOffset: 0,
    };
  },

  mounted() {
    baseNavbar.mounted?.call(this);
    this.mobileMenu = new Offcanvas(this.$refs.mobileMenu, { backdrop: false, scroll: false });
    this.$refs.mobileMenu.addEventListener('hidden.bs.offcanvas', this.mobileMenuHidden);
    this.mobileBreakpoint = window.matchMedia('(min-width: 768px)');
    this.mobileBreakpoint.addEventListener('change', this.mobileViewportChanged);
    this.themeMode = FrontendService.setThemeMode(this.themeMode);
    window.addEventListener(FrontendService.THEME_CHANGED_EVENT, this.handleThemeChanged);
    this.profileRequestTimer = setInterval(() => {
      if (!document.hidden) this.refreshProfileRequests();
    }, 30000);
    window.addEventListener('focus', this.refreshProfileRequests);
  },

  beforeUnmount() {
    clearInterval(this.profileRequestTimer);
    window.removeEventListener('focus', this.refreshProfileRequests);
    baseNavbar.beforeUnmount?.call(this);
    this.mobileBreakpoint?.removeEventListener('change', this.mobileViewportChanged);
    const menu = this.mobileMenu;
    const element = this.$refs.mobileMenu;
    element?.removeEventListener('hidden.bs.offcanvas', this.mobileMenuHidden);
    if (this.mobileMenuOpen && element) {
      element.addEventListener('hidden.bs.offcanvas', () => menu?.dispose(), { once: true });
      menu?.hide();
    } else menu?.dispose();
    window.removeEventListener(FrontendService.THEME_CHANGED_EVENT, this.handleThemeChanged);
  },

  computed: {
    profileRequestIdentity() {
      const roles = [this.$user?.role?.type, this.$user?.role?.name].map(role => String(role || '').toLowerCase().replace('_', '-'));
      return this.$user?.isAuthenticated?.value && roles.some(role => ['club-admin', 'superadmin'].includes(role))
        ? `${this.$user?.id}:${this.$user?.clubId}` : '';
    },
    profileRequestAttention() { return usePlayerProfileRequestsStore().needsAttention; },
    ...baseNavbar.computed,
    mobileBrand() {
      return this.mobileSection === 'service' ? SERVICE_BRAND : this.brand;
    },
    mobileBrandNameStyle() {
      return this.mobileSection === 'service'
        ? { '--club-main-color': 'var(--app-navbar-color)', '--club-highlight-color': 'var(--app-navbar-accent)' }
        : this.brandNameStyle;
    },
    mobileLinks() {
      const service = [
        ['Home', 'home', 'bi-house'], ['AllClubs', 'clubs', 'bi-buildings'],
        ['Chess', 'chess', 'bi-grid-3x3'],
        ['Features', 'features', 'bi-grid-3x3-gap'], ['About', 'about', 'bi-info-circle'], ['Pricing', 'pricing', 'bi-cash-coin'],
      ];
      const club = [['AllPlayers', 'players'], ['AllTournaments', 'tournaments'], ['AllSeasons', 'seasons']];
      return (this.isClubMode && this.mobileSection !== 'service' ? club : service)
        .map(([route, label, icon]) => ({ route, icon, label: this.$t(`navigation.${label}`), to: this.buildLinkTo(route) }));
    },

    /**
     * Включена ли темная тема интерфейса.
     * @returns {boolean}
     */
    isDarkTheme() {
      return this.themeMode === 'dark';
    },

    /**
     * Иконка кнопки следующей темы.
     * @returns {string}
     */
    themeIconClass() {
      return FrontendService.getThemeIconClass(this.themeMode);
    },

    /**
     * Доступная подпись кнопки темы.
     * @returns {string}
     */
    themeButtonLabel() {
      return this.$t(`theme.${this.themeMode}`);
    },

    /**
     * Ссылка логотипа: внутри клуба ведет на клубную главную, вне клуба — на главную сервиса.
     * @returns {Object}
     */
    brandHomeLink() {
      return this.isClubMode
        ? this.buildLinkTo('ClubHome', { scope: 'route' })
        : this.buildLinkTo('Home');
    },

    /**
     * Является ли текущий пользователь Superadmin.
     * @returns {boolean}
     */
    isSuperAdmin() {
      return !!this.$user?.isSuperAdmin;
    },

    /**
     * Подпись текущего источника данных.
     * @returns {string}
     */
    dataSourceLabel() {
      return this.$t(this.dataSource === 'mock' ? 'navigation.mockData' : 'navigation.database');
    },

    /**
     * Цвета названия: вне клуба используем цвета темы V2, внутри клуба — цвета клуба.
     * @returns {Object}
     */
    brandNameStyle() {
      return {
        '--club-main-color': this.isClubMode ? this.brand.mainColor : 'var(--app-navbar-color)',
        '--club-highlight-color': this.isClubMode ? this.brand.highlightColor : 'var(--app-navbar-accent)',
      };
    },
  },

  methods: {
    ...baseNavbar.methods,
    refreshProfileRequests() {
      const store = usePlayerProfileRequestsStore();
      if (!this.profileRequestIdentity) { store.reset(); return; }
      return store.refresh(this.$user.clubId, this.$user.id);
    },
    openMobileMenu() {
      this.mobileSection = '';
      this.mobileMenuOpen = true;
      this.mobileMenu.show(this.$refs.mobileMenuToggle);
    },
    closeMobileMenu() {
      this.mobileMenu?.hide();
    },
    mobileMenuHidden() {
      this.mobileMenuOpen = false;
      this.mobileSection = '';
      this.$refs.mobileMenuToggle?.focus();
      if (this.mobileLoginPending) {
        this.mobileLoginPending = false;
        baseNavbar.methods.openLoginModal.call(this);
      }
    },
    mobileViewportChanged(event) {
      if (event.matches) this.closeMobileMenu();
    },
    closeNavbar(instant = false) {
      this.closeMobileMenu();
      baseNavbar.methods.closeNavbar.call(this, instant);
    },
    mobileLogin() {
      this.mobileLoginPending = true;
      this.closeMobileMenu();
    },
    async toggleMobileSection(section) {
      if (section === 'profile' && this.mobileSection !== 'profile') {
        this.mobileProfileOffset = this.$refs.mobileProfileTrigger.getBoundingClientRect().top
          - this.$refs.mobileMenuContent.getBoundingClientRect().top - 24;
        // Apply the starting offset before Vue mounts the transitioning panel.
        await this.$nextTick();
        if (!this.mobileMenuOpen) return;
      }
      this.mobileSection = this.mobileSection === section ? '' : section;
    },
    focusMobileProfile() {
      if (!this.mobileMenuOpen) return;
      const target = this.mobileSection === 'profile' ? this.$refs.mobileProfileBack : this.$refs.mobileProfileTrigger;
      target?.focus({ preventScroll: true });
    },

    /**
     * Переключает светлую и темную тему.
     */
    toggleTheme() {
      this.themeMode = FrontendService.toggleThemeMode(this.themeMode);
    },

    /**
     * Синхронизирует локальное состояние при смене темы из другого компонента.
     * @param {CustomEvent<{ mode: 'light'|'bright'|'contrast'|'dark' }>} event
     */
    handleThemeChanged(event) {
      if (FrontendService.isValidThemeMode(event?.detail?.mode)) {
        this.themeMode = event.detail.mode;
      }
    },

    /**
     * Устанавливает глобальный источник данных для публичных страниц.
     * @param {'db'|'mock'} source - Источник данных.
     */
    setDataSource(source) {
      useDataSourceStore().setMode(source);
    },


  },
};
